import React, { useState, useEffect, useContext } from 'react';
import { Modal, Table, Button, Space, Select, message, Popconfirm, Empty, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useGetUserDeductions, useAddUserDeduction, useRemoveUserDeduction } from '../../../hooks/user/userBenefitsDeductionsQuery';
import { useGetAllDeductions } from '../../../hooks/payrollData/payrollDataQuery';
import RefreshContext from '../../../context/refreshContext';

const { Option } = Select;

const UserDeductionsModal = ({ visible, onCancel, userId }) => {
  const [selectedDeductions, setSelectedDeductions] = useState([]);
  const { flag, setFlag } = useContext(RefreshContext);
  const [localRefresh, setLocalRefresh] = useState(false);

  const { deductions: availableDeductions } = useGetAllDeductions(flag);
  const { loading, deductions: userDeductions } = useGetUserDeductions(userId, localRefresh);
  const addUserDeduction = useAddUserDeduction();
  const removeUserDeduction = useRemoveUserDeduction();

  // Reset selected deductions when modal is opened
  useEffect(() => {
    if (visible) {
      setSelectedDeductions([]);
    }
  }, [visible]);

  const handleAddDeduction = async () => {
    if (!selectedDeductions.length) {
      message.error('Please select at least one deduction to add');
      return;
    }

    try {
      await addUserDeduction({
        userId,
        deductionIds: selectedDeductions
      });
      
      message.success('Deductions added successfully');
      setSelectedDeductions([]);
      setLocalRefresh(!localRefresh);
    } catch (error) {
      message.error('Failed to add deductions');
    }
  };

  const handleRemoveDeduction = async (deductionId) => {
    try {
      await removeUserDeduction(userId, deductionId);
      message.success('Deduction removed successfully');
      setLocalRefresh(!localRefresh);
    } catch (error) {
      message.error('Failed to remove deduction');
    }
  };

  // Filter out already assigned deductions from the dropdown
  const filteredDeductions = availableDeductions.filter(
    deduction => !userDeductions.some(userDeduction => userDeduction.deduction.id === deduction.id)
  );

  const columns = [
    {
      title: 'Deduction Name',
      dataIndex: ['deduction', 'deductionName'],
      key: 'deductionName'
    },
    {
      title: 'Value',
      dataIndex: ['deduction', 'deductionValue'],
      key: 'deductionValue',
      render: (text, record) => {
        if (record.deduction.computationType === 'PERCENTAGE') {
          return `${text}%`;
        }
        return `₦${text.toLocaleString()}`;
      }
    },
    {
      title: 'Type',
      dataIndex: ['deduction', 'computationType'],
      key: 'computationType',
      render: (text) => text === 'PERCENTAGE' ? 'Percentage' : 'Fixed Amount'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to remove this deduction?"
          onConfirm={() => handleRemoveDeduction(record.deduction.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            Remove
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Modal
      title="Manage User Deductions"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <div style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%' }}>
          <Select
            mode="multiple"
            placeholder="Select deductions to add"
            style={{ width: 300 }}
            value={selectedDeductions}
            onChange={setSelectedDeductions}
            showSearch
            filterOption={(input, option) =>
              option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {filteredDeductions.map(deduction => (
              <Option key={deduction.id} value={deduction.id}>
                {deduction.deductionName} - {deduction.computationType === 'PERCENTAGE' 
                  ? `${deduction.deductionValue}%` 
                  : `₦${deduction.deductionValue.toLocaleString()}`
                }
              </Option>
            ))}
          </Select>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddDeduction}
            disabled={!selectedDeductions.length}
          >
            Add Deductions
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {userDeductions.length > 0 ? (
          <Table
            columns={columns}
            dataSource={userDeductions}
            rowKey={(record) => record.id}
            pagination={{ pageSize: 5 }}
          />
        ) : (
          <Empty description="No deductions assigned to this user" />
        )}
      </Spin>
    </Modal>
  );
};

export default UserDeductionsModal;
